/**
 * Lesson content access for the coach-mark player.
 *
 * Modules are pulled with a NON-EAGER `import.meta.glob` so the curriculum is
 * one lazy chunk per module rather than being inlined into the entry bundle.
 */

import { MODULE_REGISTRY } from '@/content/module-registry'
import type { Lang } from '@/hooks/use-lang'
import type { CoachmarkStep, Module, SubModule } from '@/schema'

// EN glob — matches *.json but explicitly NOT *.zh.json.
const moduleLoaders = import.meta.glob<{ default: Module }>([
  '/src/content/modules/*.json',
  '!/src/content/modules/*.zh.json',
])

// ZH glob — targets *.zh.json only.
const moduleLoadersZh = import.meta.glob<{ default: Module }>('/src/content/modules/*.zh.json')

/** Every sub-module in every module, flattened, with its owning module id. */
export interface TourLesson {
  moduleId: string
  moduleTitle: string
  subModule: SubModule
  steps: CoachmarkStep[]
}

function coachmarkStepsOf(subModule: SubModule): CoachmarkStep[] {
  return subModule.sections
    .flatMap((section) => section.steps)
    .filter((step): step is CoachmarkStep => step.type === 'coachmark')
}

/**
 * Whether this lesson has an UNGUIDED stage — i.e. whether finishing the guided
 * coach-mark tour alone completes it.
 *
 * `tasks[]` is optional in the schema (`src/schema/index.ts:248`) and only
 * `ordering-essentials` and `receiving-essentials` author it, five lessons
 * each. The other 19 sub-modules have no unguided stage at all, and for those
 * the guided tour IS the whole lesson — without that, they could never be
 * completed and every course percentage would be stuck below 100%.
 *
 * Both overlays already hold the sub-module in order to render, so this is a
 * lookup they have for free. It is deliberately a plain predicate over content
 * rather than state on the progress store: the store records what was true when
 * the learner finished (`StageRecord.requiresUnguided`), this answers what is
 * true in the content right now, and conflating the two is what lets a content
 * edit retroactively un-complete somebody's lesson.
 */
export function hasUnguidedStage(subModule: SubModule | null | undefined): boolean {
  return (subModule?.tasks?.length ?? 0) > 0
}

/**
 * Resolve a lesson by sub-module id. Returns null when no module contains it,
 * so the caller can render a not-found state instead of throwing.
 *
 * When `lang === 'zh'`, tries the ZH glob first and silently falls back to
 * English if no *.zh.json file contains the sub-module (D-06).
 */
export async function loadLesson(
  subModuleId: string,
  lang: Lang = 'en',
): Promise<TourLesson | null> {
  if (lang === 'zh') {
    for (const load of Object.values(moduleLoadersZh)) {
      const mod = (await load()).default
      const subModule = mod.subModules.find((s) => s.id === subModuleId)
      if (!subModule) continue
      return {
        moduleId: mod.id,
        moduleTitle: mod.title,
        subModule,
        steps: coachmarkStepsOf(subModule),
      }
    }
    // no ZH file for this module yet — fall through to EN silently
  }
  for (const load of Object.values(moduleLoaders)) {
    const mod = (await load()).default
    const subModule = mod.subModules.find((s) => s.id === subModuleId)
    if (!subModule) continue
    return {
      moduleId: mod.id,
      moduleTitle: mod.title,
      subModule,
      steps: coachmarkStepsOf(subModule),
    }
  }
  return null
}

/**
 * Result of mapping a sub-module id back to the module that owns it.
 *
 * This cannot be a bare `string | null`, because sub-module ids are not
 * guaranteed unique across the curriculum. A caller that collapses "found two"
 * into "found one" writes a completion into the wrong lesson column of the
 * system of record, permanently — so the ambiguity is surfaced in the type and
 * the caller has to decide what to do about it.
 */
export type ModuleIdLookup =
  | { readonly status: 'resolved'; readonly moduleId: string }
  /** No module contains this id. */
  | { readonly status: 'unknown' }
  /** Two or more modules contain it — `moduleIds` lists every claimant. */
  | { readonly status: 'ambiguous'; readonly moduleIds: readonly string[] }

/**
 * Find the module that owns `subModuleId`.
 *
 * Unlike `loadLesson`, this must NOT stop at the first hit — detecting
 * ambiguity means visiting every module. It reads the EN glob only: the ZH
 * files describe the same modules under the same ids, so including them would
 * add duplicate claimants rather than information.
 *
 * COST: visiting every module means fetching every module chunk that is not
 * already in the module cache — worst case ~105 kB of JSON, most of it
 * `ordering-essentials` and `receiving-essentials`. `loadLesson` will already
 * have warmed everything up to and including the lesson being played, so the
 * marginal fetch is the modules after it. The caller
 * (`lesson-progress.markDone`) does not await this, so the cost is off the
 * learner's path — but it is a real request burst at completion time, and a
 * generated `subModuleId → moduleId` index should replace this scan when the
 * content pipeline (TECH-SPEC §3.10) lands.
 */
export async function findModuleIdFor(subModuleId: string): Promise<ModuleIdLookup> {
  const moduleIds: string[] = []
  // Sequential on purpose: this scan runs un-awaited off the completion path
  // (lesson-progress.markDone), and loading one chunk at a time keeps it from
  // racing every module into memory at once. A generated id→moduleId index
  // (TECH-SPEC §3.10) should replace the scan outright.
  for (const load of Object.values(moduleLoaders)) {
    const mod = (await load()).default // NOSONAR S9382: see note above — deliberate sequential interim scan
    if (mod.subModules.some((s) => s.id === subModuleId)) moduleIds.push(mod.id)
  }

  const [first, second] = moduleIds
  if (first === undefined) return { status: 'unknown' }
  if (second !== undefined) return { status: 'ambiguous', moduleIds }
  return { status: 'resolved', moduleId: first }
}

/** Summary of one lesson, for the launcher. */
export interface LessonSummary {
  subModuleId: string
  title: string
  stepCount: number
}

/**
 * Every coach-mark lesson across every module, in content order.
 *
 * The launcher reads this rather than a hard-coded list, so adding a lesson is
 * authoring JSON — no application code changes, which is the whole point of the
 * content pipeline.
 */
export async function listLessons(): Promise<LessonSummary[]> {
  // The launcher needs every module, so fetch the chunks together instead of
  // awaiting them one per iteration; Promise.all keeps them in content order.
  const mods = await Promise.all(Object.values(moduleLoaders).map((load) => load()))
  const summaries: LessonSummary[] = []
  for (const { default: mod } of mods) {
    for (const subModule of mod.subModules) {
      const steps = coachmarkStepsOf(subModule)
      if (steps.length === 0) continue
      // Tours are orientation content — they run through the same player but
      // are not trackable lessons and do not need ZH translations to match EN.
      if (MODULE_REGISTRY[mod.id]?.subModules[subModule.id]?.type === 'tour') continue
      summaries.push({ subModuleId: subModule.id, title: subModule.title, stepCount: steps.length })
    }
  }
  return summaries
}
